import { CSSProperties, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import ebiOfficeService from "api/ebiOfficeService";
import officeService from "api/officeService";
import Button from "components/Button";
import ColorField from "components/ColorField";
import HollowButton from "components/HollowButton";
import { IEbiPortalTheme } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { samePortalTheme } from "utils/ebiPortalTheme";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import EbiPortalPreview from "./EbiPortalPreview";
import styles from "./styles.module.scss";
import { FC } from "./typings";

// Podzakładka „Wygląd”: kolor akcentu portalu petenta — kafelki gotowych wariantów (te same klucze co kolorystyka maili)
// albo własny kolor (linki, aktywna zakładka, pasek wprowadzający, znaczniki; przyciski główne portalu są czarne jak
// w projekcie). Portal w układzie z projektu Claude Design nie ma już plam tła — kolory tła zostają w zapisie wariantu
// (BE wymaga trzech kolorów), ale nie ma ich w formularzu. Makieta obok pokazuje kolor jeszcze niezapisany; zapis idzie
// do ustawień EBI, a portal czyta go z kontekstu urzędu. Nazwa urzędu i herb pochodzą z zakładki Urząd / ustawień
// podstawowych — tu tylko informacja, co petent zobaczy.

/** Akcent wariantu jako zmienna CSS miniatury portalu — odcienie ustawia arkusz. */
const sampleStyle = (theme: IEbiPortalTheme): CSSProperties =>
  ({
    "--sample-accent": theme.Accent,
  }) as CSSProperties;

const EbiAppearanceTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: settingsData } = ebiOfficeService.useGetSettings();
  const { data: officeData } = officeService.useGetOffice();
  const { data: themeData, error: themeError } = ebiOfficeService.useGetPortalTheme();

  const settings = settingsData?.data;
  const office = officeData?.data;
  const themeSettings = themeData?.data;
  const savedTheme = themeSettings?.Theme ?? null;
  const effectiveName = settings?.OfficeName?.trim() || settings?.DefaultOfficeName?.trim() || "";

  // kolory z formularza; null = jeszcze nie ruszone (makieta i pola pokazują zapisane)
  const [draft, setDraft] = useState<IEbiPortalTheme | null>(null);
  const value = draft ?? savedTheme;
  const dirty = !!draft && !samePortalTheme(draft, savedTheme);

  const presets = themeSettings?.Presets ?? [];
  const canEdit = !!themeSettings?.CanEdit;
  const canSave = canEdit && !!themeSettings?.HasSettings;
  const activeKey = value ? presets.find((preset) => samePortalTheme(preset, value))?.Key ?? null : null;

  const { mutate: saveTheme, isLoading: isSaving } = ebiOfficeService.useSavePortalTheme();

  // jak w Powiadomieniach: odpowiedź bez komunikatu = serwer bez tej trasy
  const themeErrorText = themeError
    ? getErrorMessage(themeError) || t("ebiOffice.appearance.theme.unsupported")
    : null;

  const notify = (type: ENotificationType, text: string) =>
    notification({ type, title: t("ebiOffice.appearance.theme.title"), text });

  const choosePreset = (preset: IEbiPortalTheme) => {
    if (!canEdit) return;

    setDraft({ Background1: preset.Background1, Background2: preset.Background2, Accent: preset.Accent });
  };

  const handleSave = () => {
    if (!draft) return;

    saveTheme(draft, {
      onSuccess: ({ data: saved }) => {
        queryClient.setQueryData(ebiOfficeService.queryKeys.getPortalTheme, { data: saved });
        setDraft(null);
        notify(ENotificationType.SUCCESS, t("ebiOffice.appearance.theme.saved"));
      },
      onError: (e) =>
        notify(
          ENotificationType.ERROR,
          getErrorMessage(e as IError) ||
            (e instanceof Error ? e.message : "") ||
            t("errors.somethingWentWrongShort"),
        ),
    });
  };

  return (
    <div className={styles.wide}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("ebiOffice.appearance.title")}</div>
        <div className={styles.hint}>{t("ebiOffice.appearance.description")}</div>
        <div className={effectiveName ? styles.text : styles.error}>
          {effectiveName
            ? t("ebiOffice.appearance.effectiveName", { name: effectiveName })
            : t("ebiOffice.appearance.noName")}
        </div>
        {office && (
          <div className={styles.text}>
            {office.HasEmblem ? t("ebiOffice.appearance.emblemYes") : t("ebiOffice.appearance.emblemNo")}
          </div>
        )}
      </div>

      <div className={styles.notifications}>
        <div className={styles.templateList}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t("ebiOffice.appearance.theme.title")}</div>
            <div className={styles.hint}>{t("ebiOffice.appearance.theme.description")}</div>
            {themeErrorText && <div className={styles.error}>{themeErrorText}</div>}
            {themeSettings && !canEdit && (
              <div className={styles.hint}>{t("ebiOffice.appearance.theme.readOnly")}</div>
            )}
            {themeSettings && canEdit && !themeSettings.HasSettings && (
              <div className={styles.hint}>{t("ebiOffice.appearance.theme.needsSettings")}</div>
            )}

            {value && presets.length > 0 && (
              <>
                <div className={styles.presetGrid}>
                  {presets.map((preset) => (
                    <div
                      key={preset.Key}
                      className={classNames(
                        styles.preset,
                        preset.Key === activeKey && styles.presetActive,
                        !canEdit && styles.presetDisabled,
                      )}
                      title={preset.Name}
                      onClick={() => choosePreset(preset)}
                    >
                      <div className={styles.portalSample} style={sampleStyle(preset)}>
                        <span className={styles.portalSampleHeader} />
                        <span className={styles.portalSampleBand} />
                        <span className={styles.portalSampleAccent} />
                      </div>
                      <div className={styles.presetName}>
                        {t(`ebiOffice.appearance.theme.preset.${preset.Key}`, { defaultValue: preset.Name })}
                      </div>
                    </div>
                  ))}
                </div>
                {!activeKey && <div className={styles.hint}>{t("ebiOffice.appearance.theme.customHint")}</div>}

                <div className={styles.colorFields}>
                  <ColorField
                    label={t("ebiOffice.appearance.theme.accent")}
                    value={value.Accent}
                    onChange={(color) => setDraft({ ...value, Accent: color })}
                    disabled={!canEdit}
                  />
                </div>

                {canEdit && (
                  <div className={styles.actions}>
                    {dirty && (
                      <HollowButton
                        text={t("ebiOffice.appearance.theme.cancel")}
                        onClick={() => setDraft(null)}
                        disabled={isSaving}
                      />
                    )}
                    <Button
                      text={t("ebiOffice.appearance.theme.save")}
                      onClick={handleSave}
                      disabled={!dirty || !canSave || isSaving}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {value && (
          <div className={styles.previewBox}>
            <div className={styles.previewMeta}>
              <span className={styles.previewLabel}>{t("ebiOffice.appearance.theme.preview")}</span>
            </div>
            <EbiPortalPreview
              theme={value}
              officeName={effectiveName || undefined}
              hasEmblem={!!office?.HasEmblem}
              emblemVersion={office?.EmblemVersion}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EbiAppearanceTab;
